import * as modalTypes from "../../../../constants/modalTypes";
import * as objectTypes from "../../../../constants/objectTypes";
import * as optionHelper from "../../../../helpers/optionHelper";
import * as propertyConstants from "../../../../constants/propertyConstants";
import * as translationHelper from "../../../../helpers/translationHelper";

import { Image, createEmptyImage, generateImageData, generateImageHeadings } from "../../../../types/image";
import React, { Component } from "react";

import { FileType } from "../../../../types/fileType";
import { ImageCategory } from "../../../../types/imageCategory";
import { ModalParams } from "../../../../types/modalParams";
import { ModalProperties } from "../../../../types/modalProperties";
import { TableContainer } from "../../../Table";
import { TableHeading } from "../../../../types/tableHeading";

import { t as translate } from "react-i18nify";

type Props = {
    tableConstant: string;
    objectType: objectTypes.ObjectType;
    dataPrivileges: any;
    loading: boolean;
    buttonLoading: boolean;
    categoryList: Array<ImageCategory>;
    fileTypeList: Array<FileType>;
    allList: Array<Image>;
    activeList: Array<Image>;
    columnOrder: Array<propertyConstants.Property>;
    columnVisibility: Record<propertyConstants.Property, boolean>;
    columnWidth: Record<propertyConstants.Property, number>;
    offset: number;
    page: number;
    rowCount: number;
    rowCountCustom: boolean;
    search: string | null;
    searchParams: Record<propertyConstants.Property, string>;
    showFilterRow: boolean;
    showGlobalSearch: boolean;
    sortingAsc: boolean;
    sortingCriterion: propertyConstants.Property | null;
    totalCount: number | null;
};

export class ImageTable extends Component<Props> {
    generateTableHeadings = (modalType: modalTypes.ModalType | null = null): Array<TableHeading> => {
        const propertyList: Array<propertyConstants.Property> = [
            propertyConstants.PROPERTY_NAME,
            propertyConstants.PROPERTY_FILE_TYPE_ID,
            propertyConstants.PROPERTY_IMAGE_CATEGORY_ID,
            propertyConstants.PROPERTY_IMAGE_ID
        ];
        const requiredList: Array<propertyConstants.Property> = [
            propertyConstants.PROPERTY_NAME,
            propertyConstants.PROPERTY_FILE_TYPE_ID
        ];
        const alwaysVisibleList: Array<propertyConstants.Property> = [propertyConstants.PROPERTY_NAME];
        let editableList: Array<propertyConstants.Property> = [propertyConstants.PROPERTY_NAME];
        let visibleList: Array<propertyConstants.Property> = [...propertyList];

        visibleList.splice(3, 0, propertyConstants.PROPERTY_URL);

        if (modalType) {
            editableList.push(propertyConstants.PROPERTY_URL);
            editableList.push(propertyConstants.PROPERTY_IMAGE_CATEGORY_ID);
            editableList.push(propertyConstants.PROPERTY_IMAGE_ID);
        }

        if (modalType === null || modalType === modalTypes.MODAL_IMAGES_EDIT) {
            visibleList.splice(3, 0, propertyConstants.PROPERTY_WIDTH);
            visibleList.splice(4, 0, propertyConstants.PROPERTY_HEIGHT);
            visibleList.splice(5, 0, propertyConstants.PROPERTY_LENGTH);
        }

        if (modalType === modalTypes.MODAL_IMAGES_ADD) {
            editableList.push(propertyConstants.PROPERTY_FILE_TYPE_ID);
        }

        if (modalType === modalTypes.MODAL_IMAGES_ADD_MORE) {
            visibleList = [propertyConstants.PROPERTY_IMAGE_ID];
            editableList = [propertyConstants.PROPERTY_IMAGE_ID];
        }

        let orderList = [...visibleList];

        if (modalType === null) {
            orderList = optionHelper.handleTableHeadingOrder(visibleList, this.props.columnOrder);
            visibleList = optionHelper.handleTableHeadingVisibility(visibleList, this.props.columnVisibility);
        }

        return generateImageHeadings(
            this.props.objectType,
            modalType,
            this.props.categoryList,
            this.props.fileTypeList,
            orderList,
            visibleList,
            editableList,
            requiredList,
            alwaysVisibleList,
            this.props.columnWidth
        );
    };

    generateAddModalParams = (): ModalParams => {
        return new ModalParams(
            modalTypes.ADD_MODAL,
            this.props.objectType,
            translate("image.addImage"),
            createEmptyImage(),
            this.generateTableHeadings(modalTypes.MODAL_IMAGES_ADD)
        );
    };

    generateAddMoreModalParams = (): ModalParams => {
        return new ModalParams(
            modalTypes.ADD_MODAL,
            objectTypes.IMAGE_DIRECTORY,
            translate("image.addImageMore"),
            createEmptyImage(),
            this.generateTableHeadings(modalTypes.MODAL_IMAGES_ADD_MORE)
        );
    };

    generateEditModalParams = (): ModalParams | null => {
        const { activeList } = this.props;

        const activeData = generateImageData(activeList);

        if (activeData.length) {
            return new ModalParams(
                modalTypes.EDIT_MODAL,
                this.props.objectType,
                translate("image.editImage"),
                activeData[0],
                this.generateTableHeadings(modalTypes.MODAL_IMAGES_EDIT)
            );
        }

        return null;
    };

    render(): JSX.Element {
        const modalProperties: ModalProperties = {
            addModalType: modalTypes.MODAL_IMAGES_ADD,
            addModalParams: this.generateAddModalParams(),
            addMoreModalType: modalTypes.MODAL_IMAGES_ADD_MORE,
            addMoreModalParams: this.generateAddMoreModalParams(),
            editModalType: modalTypes.MODAL_IMAGES_EDIT,
            editModalParams: this.generateEditModalParams(),
            imageCategoriesModalParams: {},
            imageCategoriesModalType: modalTypes.MODAL_IMAGE_CATEGORIES_TABLE
        };

        return (
            <div className="height-100">
                <TableContainer
                    className={"table-with-filter"}
                    tableConstant={this.props.tableConstant}
                    objectType={this.props.objectType}
                    dataPrivileges={this.props.dataPrivileges}
                    loading={this.props.loading}
                    buttonLoading={this.props.buttonLoading}
                    title={translationHelper.getTitleSectionTranslation(this.props.objectType)}
                    titleClassName={"main-title"}
                    headings={this.generateTableHeadings()}
                    data={generateImageData(this.props.allList)}
                    activeData={this.props.activeList}
                    offset={this.props.offset}
                    page={this.props.page}
                    rowCount={this.props.rowCount}
                    rowCountCustom={this.props.rowCountCustom}
                    search={this.props.search}
                    searchParams={this.props.searchParams}
                    showFilterRow={this.props.showFilterRow}
                    showGlobalSearch={this.props.showGlobalSearch}
                    sortingAsc={this.props.sortingAsc}
                    sortingCriterion={this.props.sortingCriterion}
                    totalCount={this.props.totalCount}
                    modalProperties={modalProperties}
                />
            </div>
        );
    }
}
